import { useContext, useState, useEffect } from 'react';
import { useLazyQuery, useQuery } from '@apollo/react-hooks';
import { UserContext } from 'contexts/user-context';
import { ThemeContext } from 'contexts/theme-context';
import { HexomaticUserContext } from 'contexts/hexomatic-user';

import {
  BILING_DATES,
  GET_HEXOMATIC_USER,
  GET_USER_WATCH_SETTINGS_QUERY,
  HEXOSPARK_USER,
  LIST_PREMIUM_CREDITS,
  USER_QUERY,
  GET_RENEWAL_DATE,
} from 'graphql/queries';
// import { Link } from 'react-router-dom';

import hexowatchLogo from 'assets/logos/hexowatch-logo-rectangle.svg';
import hexowatchLogoWhite from 'assets/logos/hexowatch-logo-rectangle-white.svg';

import hexomaticLogo from 'assets/logos/hexomatic-logo-rectangle.svg';
import hexomaticLogoWhite from 'assets/logos/hexomatic-logo-rectangle-white.svg';

import hexosparkLogo from 'assets/logos/hexospark-logo-rectangle.svg';
import hexosparkLogoWhite from 'assets/logos/hexospark-logo-rectangle-white.svg';

import Plan from './plan-info';

import '../styles.scss';

const packInfo = [
  {
    name: 'FREE',
    maxInterval: '12 hours',
    integrations: false,
    reports: false,
    server: false,
    totalChecks: 75,
  },
  {
    name: 'STANDARD',
    maxInterval: '30 min',
    integrations: true,
    reports: true,
    server: false,
    totalChecks: 2000,
  },
  {
    name: 'PRO',
    maxInterval: '15 min',
    integrations: true,
    reports: true,
    server: false,
    totalChecks: 4500,
  },
  {
    name: 'BUSINESS',
    maxInterval: '5 min',
    integrations: true,
    reports: true,
    server: true,
    totalChecks: 10000,
  },
  {
    name: 'BUSINESS+',
    maxInterval: '5 min',
    integrations: true,
    reports: true,
    server: true,
    totalChecks: null,
  },
  {
    name: 'MICROSOFT',
    maxInterval: '5 min',
    integrations: true,
    reports: true,
    server: true,
    totalChecks: 150000,
  },
];

const MyPlans = () => {
  const { user, setUser } = useContext(UserContext);
  const { hexomaticUser, setHexomaticUser } = useContext(HexomaticUserContext);
  const [userDataLocal, setUserDataLocal] = useState(user);

  const [pricingPackage, setPricingPackage] = useState('');
  const [hexosparkPlanInfo, setHexosparkPlanInfo] = useState<any>(null);
  const [pricingPackageInterval, setPricingPackageInterval] = useState('');
  const currentPackage = packInfo.find(item => item.name === pricingPackage);
  const [leftLimits, setLeftLimits] = useState<string | number>('N/A');
  const [monthlyLimit, setMonthlyLimit] = useState(0);
  const [billingInfo, setBillingInfo] = useState<any>(null);
  const [additionalScans, setAdditionalScans] = useState(0);
  const [view_credit, setViewCredit] = useState(false);
  const [billingDates, setBillingDates] = useState<null | {
    hexowatch: any;
    hexomatic: any;
    hexospark: any;
    premium_credits: any;
  }>(null);

  const [loadUserInfo, { data: userData }] = useLazyQuery(USER_QUERY, {
    fetchPolicy: 'no-cache',
    onCompleted(userData) {
      if (userData && userData.User && userData.User.get) {
        setUser({ ...userData.User.get, loaded: true });
        setUserDataLocal({ ...userData.User.get, loaded: true });
      }
    },
  });

  const [hexomaticUserInfo, { data: hexomaticUserData }] = useLazyQuery(GET_HEXOMATIC_USER, {
    fetchPolicy: 'no-cache',
    onCompleted(hexomaticUserData) {
      if (hexomaticUserData?.HexomaticUser?.getByLegacyId) {
        setHexomaticUser({ ...hexomaticUserData.HexomaticUser.getByLegacyId, photo: user?.photo });
      }
    },
  });

  const { data: premiumCredits, loading: premiumCreditsLoading } = useQuery(LIST_PREMIUM_CREDITS);
  const {
    data: settingsQuery,
    loading,
    refetch,
  } = useQuery(GET_USER_WATCH_SETTINGS_QUERY, {
    fetchPolicy: 'no-cache',
  });
  const {
    data: hexosparkUser,
    loading: hexosparkLoading,
    refetch: hexosparkUserRefetch,
  } = useQuery(HEXOSPARK_USER, {
    fetchPolicy: 'no-cache',
  });

  const {
    data: billingData,
    loading: billingLoading,
    refetch: refetchBillingInfo,
  } = useQuery(BILING_DATES, {
    fetchPolicy: 'no-cache',
    variables: {
      settings: {
        productType: '',
      },
    },
  });

  const {
    data: renewalDate,
    loading: renewalDateLoading,
    refetch: refetchRenewalDate,
  } = useQuery(GET_RENEWAL_DATE, {
    variables: {
      settings: {
        hexomatic: true,
        hexowatch: false,
        hexometer: false,
        premium_credits: true,
      },
    },
  });

  useEffect(() => {
    loadUserInfo();
    refetch();
  }, []);

  useEffect(() => {
    setUserDataLocal(user);
  }, [user]);

  useEffect(() => {
    if (billingData?.Billing?.retrieveUpcomingInvoice?.packagesBillingInfo) {
      setBillingDates(billingData.Billing.retrieveUpcomingInvoice.packagesBillingInfo);
    }
  }, [billingData]);

  useEffect(() => {
    if (settingsQuery?.UserWatchSettings?.get) {
      const settings = settingsQuery.UserWatchSettings.get;

      if (settings) {
        settings.config &&
          settings.config.additional_scans_count &&
          setAdditionalScans(settings.config.additional_scans_count);
        settings.billing_info && setBillingInfo(settings.billing_info);
        settings.left_monthly && setLeftLimits(settings.left_monthly);
        settings.monthly_limit && setMonthlyLimit(settings.monthly_limit);
      }

      if (settings && settings.pricing_package && settings.pricing_package_interval) {
        settings.pricing_package.indexOf('BUSINESS_SCANS_10000') > -1 &&
        settings.pricing_package.indexOf('BUSINESS_SCANS_100000') === -1
          ? setPricingPackage('BUSINESS')
          : settings.pricing_package.indexOf('BUSINESS_SCANS') > -1
          ? setPricingPackage('BUSINESS+')
          : setPricingPackage(settings.pricing_package);
        settings.pricing_package.indexOf('FREE') > -1
          ? setPricingPackageInterval('Forever free')
          : setPricingPackageInterval(settings.pricing_package_interval);
      }
    }
  }, [settingsQuery]);

  useEffect(() => {
    if (
      hexosparkUser?.HexosparkUser?.getHexosparkUser?.hsUser?.pricing_package &&
      hexosparkUser.HexosparkUser.getHexosparkUser.hsUser.pricing_package !== 'FREE'
    ) {
      setHexosparkPlanInfo({
        pricing_package: hexosparkUser.HexosparkUser.getHexosparkUser.hsUser.pricing_package,
        pricing_package_count: hexosparkUser.HexosparkUser.getHexosparkUser.hsUser.pricing_package_count,
        pricing_package_interval: hexosparkUser.HexosparkUser.getHexosparkUser.hsUser.pricing_package_interval,
        subscription_status: hexosparkUser.HexosparkUser.getHexosparkUser.hsUser.subscription_status,
        paypal_subscription_id: hexosparkUser.HexosparkUser.getHexosparkUser.hsUser.paypal_subscription_id,
        billing_info: hexosparkUser.HexosparkUser.getHexosparkUser.hsUser.billing_info,
        storage: hexosparkUser.HexosparkUser.getHexosparkUser.hsUser.settings.storage,
        teamAccess: hexosparkUser.HexosparkUser.getHexosparkUser.hsUser.settings.teamAccess,
        workspaces: hexosparkUser.HexosparkUser.getHexosparkUser.hsUser.settings.workspaces,
        emailAccounts: hexosparkUser.HexosparkUser.getHexosparkUser.hsUser.settings.emailAccounts,
        totalPromptsCount: hexosparkUser.HexosparkUser.getHexosparkUser.hsUser.settings.totalPromptsCount,
        usedPromptsCount: hexosparkUser.HexosparkUser.getHexosparkUser.hsUser.settings.usedPromptsCount,
      });
    }
  }, [hexosparkUser]);

  return (
    <div className="col-12">
      <Plan
        name={hexomaticUser && hexomaticUser.pricing_package ? hexomaticUser.pricing_package : 'N/A'}
        logo={hexomaticLogo}
        whiteLogo={hexomaticLogoWhite}
        packageInfo={hexomaticUser?.pricing_package_interval || 'N/A'}
        href="https://hexomatic.com/pricing/"
        showBillingInfo={billingDates?.hexomatic}
        billingInfo={billingDates?.hexomatic}
        hexomatic
        loading={loading || billingLoading || !user || !user.id}
        history={
          !!(
            hexomaticUser.pricing_package &&
            hexomaticUser.pricing_package !== 'FREE' &&
            hexomaticUser.pricing_package_interval !== 'LTD' &&
            !(hexomaticUser.paypal_subscription_id && hexomaticUser.subscription_status === 'trial')
          )
        }
        productType="hexomatic"
        productName="Hexomatic"
        cancel={
          !!(
            hexomaticUser.pricing_package &&
            hexomaticUser.pricing_package !== 'FREE' &&
            hexomaticUser.pricing_package_interval !== 'LTD'
          )
        }
        // cancel={
        //   !!(
        //     hexomaticUser.pricing_package &&
        //     hexomaticUser.pricing_package !== 'FREE' &&
        //     hexomaticUser.pricing_package_interval !== 'LTD' &&
        //     !hexomaticUser.paypal_subscription_id
        //   )
        // }
        status={hexomaticUser.subscription_status}
        refetchAfterCancelation={hexomaticUserInfo}
        renewalDate={hexomaticUser?.billing_info?.next_billing_date}
        showResume={!hexomaticUser?.paypal_subscription_id}
      >
        <>
          {/* <p className="plans-info-line">
                <span className="info-name">Count: </span>
                <span className="info-value">{hexomaticUser.pricing_package_count || 'N/A'}</span>
              </p> */}
          <p className="plans-info-line">
            <span className="info-name">Monthly automation credits:</span>
            {hexomaticUser.automation_credit ? (
              <span className="info-value">
                {hexomaticUser.automation_credit - (hexomaticUser.left_automation_credit || 0)} used out of{' '}
                {hexomaticUser.automation_credit}
              </span>
            ) : null}
          </p>
          <>
            <p className="auto-credits-title title-secondary">
              Your allowance will renew{' '}
              {!renewalDate?.User?.getPackageRenewalDate.hexomatic
                ? ' ' + 'today'
                : ' ' +
                  'in ' +
                  renewalDate?.User?.getPackageRenewalDate.hexomatic +
                  ` day${renewalDate?.User?.getPackageRenewalDate.hexomatic !== 1 ? 's' : ''}`}
            </p>
            {hexomaticUser?.billing_info?.next_billing_date ? <hr className="m-0 mt-2 mb-2" /> : null}
          </>
        </>
      </Plan>
      {hexosparkPlanInfo?.pricing_package && (
        <Plan
          name={
            hexosparkPlanInfo?.pricing_package === 'FREE'
              ? 'Free'
              : hexosparkPlanInfo?.pricing_package === 'SOLO'
              ? 'Solo'
              : hexosparkPlanInfo?.pricing_package === 'TEAM'
              ? 'Team'
              : hexosparkPlanInfo?.pricing_package_count && hexosparkPlanInfo?.pricing_package_count <= 4
              ? 'Solo'
              : hexosparkPlanInfo?.pricing_package_count && hexosparkPlanInfo?.pricing_package_count > 4
              ? 'Team'
              : 'N/A'
          }
          logo={hexosparkLogo}
          whiteLogo={hexosparkLogoWhite}
          packageInfo={hexosparkPlanInfo?.pricing_package_interval || 'N/A'}
          href="https://hexospark.com/pricing/"
          loading={loading || billingLoading || !user || !user.id || hexosparkLoading}
          showBillingInfo={billingDates?.hexospark}
          billingInfo={billingDates?.hexospark}
          history={
            !!(
              hexosparkPlanInfo?.pricing_package &&
              hexosparkPlanInfo?.pricing_package !== 'FREE' &&
              hexosparkPlanInfo?.pricing_package_interval !== 'LTD' &&
              !(hexosparkPlanInfo?.paypal_subscription_id && hexosparkPlanInfo?.subscription_status === 'trial')
            )
          }
          productType="hexospark"
          productName="Hexospark"
          cancel={
            !!(
              hexosparkPlanInfo?.pricing_package &&
              hexosparkPlanInfo?.pricing_package !== 'FREE' &&
              hexosparkPlanInfo?.pricing_package_interval !== 'LTD'
            )
          }
          status={hexosparkPlanInfo?.subscription_status}
          refetchAfterCancelation={hexosparkUserRefetch}
          renewalDate={hexosparkPlanInfo?.billing_info?.next_billing_date}
          showResume={!hexosparkPlanInfo?.paypal_subscription_id}
        >
          <>
            <p className="plans-info-line">
              <span className="info-name">Email inboxes: </span>
              <span className="info-value">{hexosparkPlanInfo?.emailAccounts || 0 || 'N/A'}</span>
            </p>
            <p className="plans-info-line">
              <span className="info-name">Account storage: </span>
              <span className="info-value">
                {hexosparkPlanInfo?.storage ? hexosparkPlanInfo?.storage / 1_000_000_000 : 0} GB
              </span>
            </p>
            {hexosparkPlanInfo?.teamAccess >= 2 ? (
              <p className="plans-info-line">
                <span className="info-name">Team seats: </span>
                <span className="info-value">
                  1+{hexosparkPlanInfo.teamAccess - 1} Team seats ({hexosparkPlanInfo.teamAccess} total users)
                </span>
              </p>
            ) : null}
            {hexosparkPlanInfo?.workspaces ? (
              <p className="plans-info-line">
                <span className="info-name">Workspaces: </span>
                <span className="info-value">{hexosparkPlanInfo?.workspaces || 'N/A'}</span>
              </p>
            ) : null}
            {hexosparkPlanInfo?.totalPromptsCount ? (
              <p className="plans-info-line">
                <span className="info-name">Icebreakers: </span>
                <span className="info-value">{hexosparkPlanInfo.totalPromptsCount || 'N/A'}</span>
              </p>
            ) : null}
          </>
        </Plan>
      )}
      <Plan
        name={`${pricingPackage ? pricingPackage.toLowerCase() + (monthlyLimit === 500 ? 'Trial' : '') : 'N/A'}`}
        logo={hexowatchLogo}
        whiteLogo={hexowatchLogoWhite}
        packageInfo={pricingPackageInterval ? pricingPackageInterval : 'N/A'}
        href="https://hexowatch.com/pricing/"
        showBillingInfo={pricingPackageInterval !== 'LTD' && billingInfo && pricingPackage !== 'FREE'}
        billingInfo={billingDates?.hexowatch}
        loading={loading || billingLoading || !user || !user.id}
        history={
          !!(
            pricingPackage &&
            pricingPackage !== 'FREE' &&
            pricingPackageInterval !== 'LTD' &&
            !(
              settingsQuery?.UserWatchSettings?.get?.paypal_subscription_id &&
              settingsQuery?.UserWatchSettings?.get?.subscription_status === 'trial'
            )
          )
        }
        productType="hexowatch"
        productName="Hexowatch"
        cancel={!!(pricingPackage && pricingPackage !== 'FREE' && pricingPackageInterval !== 'LTD')}
        // cancel={
        //   !!(
        //     pricingPackage &&
        //     pricingPackage !== 'FREE' &&
        //     pricingPackageInterval !== 'LTD' &&
        //     !settingsQuery?.UserWatchSettings?.get.paypal_subscription_id
        //   )
        // }
        status={settingsQuery?.UserWatchSettings?.get?.subscription_status}
        refetchAfterCancelation={refetch}
        renewalDate={settingsQuery?.UserWatchSettings?.get?.billing_info?.next_billing_date}
        showResume={!settingsQuery?.UserWatchSettings?.get?.paypal_subscription_id}
      >
        <>
          <p className="plans-info-line">
            <span className="info-name">Checks per month:</span>
            <span className="info-value">
              {leftLimits} left out of {monthlyLimit + additionalScans}
            </span>
          </p>
          <p className="plans-info-line">
            <span className="info-name">Page check frequency:</span>
            <span className="info-value">{currentPackage && currentPackage.maxInterval}</span>
          </p>
        </>
      </Plan>
      <Plan
        name={''}
        packageInfo={String(userDataLocal.premium_credits_pricing_package_interval) || ''}
        showBillingInfo={billingDates?.premium_credits}
        billingInfo={billingDates?.premium_credits}
        loading={loading || billingLoading || premiumCreditsLoading || !user || !user.id}
        premiumCredits
        history={
          // !userDataLocal?.premium_credits_paypal_subscription_id &&
          (billingDates && billingDates.premium_credits && billingDates.premium_credits.next_billing_date) ||
          userDataLocal?.premium_credit_subscription_status === 'canceled'
        }
        productType="premium_credits"
        productName="premium credits"
        refetch={refetchBillingInfo}
        refetchAfterCancelation={loadUserInfo}
        cancel={
          !!(
            userDataLocal?.premium_credits_pricing_package !== 'FREE' &&
            userDataLocal?.premium_credits_pricing_package_interval !== 'LTD' &&
            userDataLocal.premium_credits_pricing_package !== 'PC_15900' &&
            userDataLocal.premium_credits_pricing_package !== 'PC_24900' &&
            userDataLocal.premium_credits_pricing_package !== 'PC_49900'
          )
        }
        hidePlanType={
          userDataLocal.premium_credits_pricing_package === 'PC_15900' ||
          userDataLocal.premium_credits_pricing_package === 'PC_24900' ||
          userDataLocal.premium_credits_pricing_package === 'PC_49900'
        }
        // cancel={
        //   !!(
        //     user?.premium_credits_pricing_package !== 'FREE' &&
        //     user?.premium_credits_pricing_package_interval !== 'LTD' &&
        //     !user?.premium_credits_paypal_subscription_id
        //   )
        // }
        status={userDataLocal?.premium_credit_subscription_status}
        renewalDate={userDataLocal?.premium_credit_subscription_cancel_at}
        showResume={!userDataLocal?.premium_credits_paypal_subscription_id}
      >
        <>
          <p className="plans-info-line">
            <span className="info-name">Monthly premium credits:</span>
            {userDataLocal?.premium_credits_pricing_package === 'FREE' ? (
              <span className="info-value">0</span>
            ) : (
              <span className="info-value">
                {userDataLocal?.left_premium_credits?.toFixed(2) || 0} left out of{' '}
                {userDataLocal?.premium_credits_pricing_package &&
                  premiumCredits?.Billing?.listPremiumCredits?.premiumCredits?.find(
                    (item: any) =>
                      item.name === userDataLocal.premium_credits_pricing_package && item.productType === 'STRIPE',
                  )?.limits.premium_credits_count}
              </span>
            )}
          </p>
          <p className="plans-info-line">
            <span className="info-name">Bonus one-time credits:</span>
            <span className="info-value">
              {(hexomaticUser?.left_premium_automation_credit &&
                +hexomaticUser?.left_premium_automation_credit?.toFixed(2)) ||
                0}
            </span>
          </p>
          {renewalDate?.User?.getPackageRenewalDate?.premium_credits && (
            <>
              <p className="auto-credits-title title-secondary pt-1">
                Premium credits will renew{'  '}
                {!renewalDate?.User?.getPackageRenewalDate?.premium_credits
                  ? 'today, '
                  : ' ' +
                    'in ' +
                    renewalDate?.User?.getPackageRenewalDate?.premium_credits +
                    ` day${renewalDate?.User?.getPackageRenewalDate?.premium_credits !== 1 ? 's' : ''}`}
              </p>
              <hr className="m-0 mt-2 mb-2" />
            </>
          )}
        </>
      </Plan>
    </div>
  );
};

export default MyPlans;
